import React from "react";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../enviroment";
import { showErrorToast } from "../Toast/Toast";

interface RequireSelectionProps {
  // Number of selected rows behind the wrapped (disabled) button.
  count: number;
  // Extra tooltip / toast text that wins over the "select first" hint when
  // there IS a selection (the design list's "drafts cannot be ordered").
  blockedHint?: string;
  children: React.ReactElement<{ className?: string }>;
}

// List fixes (Clarity: 450 dead clicks on "Move selected (0)" / "Add to
// Order (0)"): a disabled button swallows every click, so the wrapper span
// takes it and explains what to do - tooltip on hover, toast on click. The
// button itself stays disabled. Browsers never dispatch click events on a
// disabled form control - not even to its ancestors - so the disabled button
// gets pointer-events:none, which lets the click (and the hover) fall
// through to the span; the span keeps the not-allowed cursor so the button
// still looks disabled. Off = the child as-is, byte for byte.
export function RequireSelection({
  count,
  blockedHint,
  children,
}: RequireSelectionProps) {
  const { t } = useTranslation();
  if (!enviroment.uxListFixesEnabled) return children;
  const hint =
    count === 0 ? t("ux.selectItemsFirst") : (blockedHint ?? "");
  const childClassName = children.props.className;
  const child = React.cloneElement(children, {
    className: childClassName
      ? `${childClassName} disabled:pointer-events-none`
      : "disabled:pointer-events-none",
  });
  return (
    <Tooltip title={hint}>
      <span
        data-testid="require-selection"
        className={hint ? "cursor-not-allowed" : undefined}
        onClick={() => {
          if (hint) showErrorToast(hint);
        }}
      >
        {child}
      </span>
    </Tooltip>
  );
}

// Button label without the "(0)" while nothing is selected.
export function countLabel(
  count: number,
  withCount: string,
  base: string,
): string {
  if (!enviroment.uxListFixesEnabled) return withCount;
  return count > 0 ? withCount : base;
}
